import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { camelizeKeys, decamelizeKeys } from "humps";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import qs from 'qs';

import useApp, { ToastTypes } from "hooks/useApp";
import useAuth from "hooks/useAuth";

export interface Response { }

axios.defaults.baseURL = process.env.REACT_APP_SERVER_URL;

const useAxios = ({
  config,
  callOnInit = true,
  successCallback = undefined,
  errorCallback = undefined,
  finalCallback = undefined,
}: {
  config: AxiosRequestConfig;
  callOnInit?: boolean;
  successCallback?: Function;
  errorCallback?: Function;
  finalCallback?: Function;
}) => {
  const history = useHistory();
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(callOnInit);
  const [loaded, setLoaded] = useState(false);
  const { setToastMessage } = useApp();
  const { logout } = useAuth();

  const fetchData = (
    override: AxiosRequestConfig = {},
    onSuccess?: Function,
    onError?: Function,
    onCallEnd?: Function
  ) => {
    const token = localStorage.getItem("token");
    setLoading(true);
    let finalConfig = { ...config, ...override };
    if (onSuccess) {
      successCallback = onSuccess;
    }
    if (onError) {
      errorCallback = onError;
    }
    if (onCallEnd) {
      finalCallback = onCallEnd;
    }
    if (!finalConfig.headers) {
      finalConfig.headers = {};
    }
    if (token) {
      finalConfig.headers["Authorization"] = `Bearer ${token}`;
    } else {
      // Clear a token
      delete finalConfig.headers["Authorization"];
    }
    if (finalConfig.data && !(finalConfig.data instanceof FormData)) {
      finalConfig.data = decamelizeKeys(finalConfig.data);
    }

    if (finalConfig.url && finalConfig.url.slice(-1) !== "/") {
      finalConfig.url += "/";
    }
    if (finalConfig.params) {
      const paramsString = qs.stringify(decamelizeKeys(finalConfig.params), { arrayFormat: 'repeat', encode: false });

      if (finalConfig.url && finalConfig.url.includes("?")) {
        finalConfig.url += '&' + paramsString;
      } else {
        finalConfig.url += '?' + paramsString;
      }

      delete finalConfig.params;
    }

    axios(finalConfig)
      .then((res: AxiosResponse<Response>) => {
        if (res.data && res.headers["content-type"] === "application/json") {
          res.data = camelizeKeys(res.data);
        }
        setResponse(res.data);
        if (successCallback) {
          successCallback({
            response: res.data,
            responseHeaders: res.headers,
            loading,
            setToastMessage,
          });
        }
      })
      .catch(({ response }: AxiosError<any>) => {
        let error = response?.data?.message ?? response?.data?.error;
        if (typeof error !== "string" || !response) {
          if (Number(response?.status) >= 500) {
            error = "Internal Server Error";
          } else if (Number(response?.status) === 405) {
            error = "Method Not Allowed!";
          } else {
            error = "Oops! Something went wrong. Please try again!";
          }
        }
        if (errorCallback) {
          errorCallback({
            error,
            setToastMessage,
            status: response?.status,
            dataError: response?.data?.error,
            data: camelizeKeys(response?.data?.data || {}),
          });
        } else {
          if (response?.status === 401) {
            logout();
            history.replace("/login");
            if (typeof setToastMessage === "function") {
              setToastMessage({
                type: ToastTypes.ERROR,
                message: "Session Expired",
              });
            }
            return;
          }

          console.warn("Axios Error: ", error);
          if (typeof setToastMessage === "function") {
            setToastMessage({
              type: ToastTypes.ERROR,
              message: error,
            });
          }
        }
      })
      .finally(() => {
        setLoading(false);
        setLoaded(true);
        if (finalCallback) {
          finalCallback();
        }
      });
  };

  useEffect(() => {
    if (callOnInit) {
      fetchData();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [callOnInit]);

  return { response, loading, fetchData, loaded };
};

export default useAxios;
